import { useMemo } from "react";
import type { DocHandle } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { effective, type SignalRule, type Signals } from "../core/signals";
import { useSignalValues } from "../app/signals";
import type { SignalDef } from "./describe";

interface SettingsSlice {
  signals?: Record<string, SignalDef>;
  signalRules?: (SignalRule & { id?: string })[];
}

/** Definitions and rules from the core doc, values from the server, combined for `nowMs` (ADR 0016). */
export function useSignalContext(
  handle: DocHandle | null | undefined,
  workspaceId: string | null,
  nowMs: number,
) {
  const slice = useDocSelector(handle, (doc) => {
    const s = doc.getMap("settings").toJSON() as SettingsSlice;
    return JSON.stringify({ signals: s.signals ?? {}, signalRules: s.signalRules ?? [] });
  });
  const values = useSignalValues(workspaceId);
  return useMemo(() => {
    const parsed = JSON.parse(slice ?? "{}") as SettingsSlice;
    const definitions = parsed.signals ?? {};
    const signals: Signals = effective(values, definitions, nowMs);
    return { definitions, rules: parsed.signalRules ?? [], signals, raw: values };
  }, [slice, values, nowMs]);
}
